import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchLiveNow,
  fetchLiveUsers,
  launchLiveCampaign,
  notifyLiveUsers,
} from '../api/adminApi';
import {
  AdminUserDTO,
  CampaignLaunchBody,
  CampaignSummary,
  LiveNow,
  SegmentDescription,
  SegmentPreview,
  SystemNotificationOk,
  SystemNotificationResult,
} from '../types';
import StartCampaignModal from './StartCampaignModal';
import StartNotificationModal from './StartNotificationModal';
import ProfileImage from '../../../components/account/ProfileImage';
import { getSmartProfileUrl } from '../../../services/apiService';
import LiquidGlass from '../../../components/ui/glass/LiquidGlass';
import { useSheetRadius } from '../../../hooks/useSheetRadius';
import styles from './active-users-pill.module.scss';

interface ActiveUsersPillProps {
  /** Polling cadence in ms. Defaults to 30s; bumped to 60s when tab is hidden. */
  intervalMs?: number;
  /** Render compactly as a single inline pill (e.g. in the global top navbar). */
  compact?: boolean;
  /** Initial window the names list reflects. Defaults to 5 minutes. */
  windowMinutes?: number;
  /**
   * Pin the popover to a specific edge of the trigger. Defaults to
   * {@code right} so the panel hangs left under the pill in the
   * navbar's right cluster. Pass {@code left} when the pill sits on
   * the left side of a container so the popover doesn't overflow
   * off-screen.
   */
  popoverAlign?: 'left' | 'right';
}

/**
 * Window selector keys. Each maps to the count field on
 * {@link LiveNow} and to a request to {@code /analytics/live-users}
 * with the right {@code minutes} param. Keeping the keys here means
 * the user can toggle between "active 5m / last hour / today" without
 * a separate state machine — clicking the count IS the toggle.
 */
type ActiveWindow = '5m' | '1h' | 'today';

const WINDOW_LABEL: Record<ActiveWindow, string> = {
  '5m': 'last 5 min',
  '1h': 'last 1 hour',
  today: 'today',
};

/** Compute the request {@code minutes} for a given window key. */
function windowToMinutes(w: ActiveWindow): number {
  if (w === '5m') return 5;
  if (w === '1h') return 60;
  // "Today" = since midnight in the *client's* local timezone, so what
  // shows up here matches what the admin perceives as "today" on the
  // wall clock. Backend caps at 1440 minutes anyway.
  const now = new Date();
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.max(1, Math.floor((now.getTime() - midnight.getTime()) / 60000));
}

const fmtAsOf = (iso: string | undefined): string => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return '';
  }
};

const fmtAgo = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const diffSec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  const min = Math.floor(diffSec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
};

/**
 * Compact signup-age formatter — "new" highlight badge for sub-24h
 * accounts so a recently-active user who just joined stands out from a
 * recently-active long-time user.
 */
const fmtSignupAge = (
  iso: string | null | undefined,
): { text: string; isFresh: boolean } | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const diffSec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  const day = Math.floor(diffSec / 86400);
  if (day === 0) {
    const hr = Math.floor(diffSec / 3600);
    return { text: hr <= 1 ? 'new today' : `${hr}h old`, isFresh: true };
  }
  if (day < 7) return { text: `${day}d old`, isFresh: day <= 1 };
  if (day < 30) return { text: `${Math.floor(day / 7)}w old`, isFresh: false };
  if (day < 365) return { text: `${Math.floor(day / 30)}mo old`, isFresh: false };
  return { text: `${Math.floor(day / 365)}y old`, isFresh: false };
};

const DEFAULT_PROFILE_PIC =
  'https://storage.googleapis.com/assestsbookiebrawler/icons/profile-default.png';

const userPictureUrl = (u: AdminUserDTO): string =>
  u.picture && u.picture !== 'null' ? u.picture : DEFAULT_PROFILE_PIC;

const UserProfileAvatarLink: React.FC<{
  user: AdminUserDTO;
  size?: number;
  showLiveDot?: boolean;
  className?: string;
}> = ({ user, size = 28, showLiveDot = true, className }) => {
  const label = user.firstname || user.handle || `#${user.id}`;
  return (
    <Link
      to={getSmartProfileUrl({ id: user.id, handle: user.handle ?? undefined })}
      className={`${styles.profileLink}${showLiveDot ? ` ${styles.profileLinkLive}` : ''}${
        className ? ` ${className}` : ''
      }`}
      title={`${label} — public profile`}
      aria-label={`Public profile for ${label}`}
      onClick={(e) => e.stopPropagation()}
    >
      <ProfileImage imageUrl={userPictureUrl(user)} size={size} />
    </Link>
  );
};

/**
 * Polls {@code /analytics/live-now} and renders a pulsing real-time
 * indicator of currently-active users on the platform, with an
 * expandable list of who is actually active.
 *
 * <p><b>Naming.</b> Previously called "live now"; renamed to "active"
 * to disambiguate from the broadcast/YouTube "Live" pill that appears
 * in the same navbar, and because counts come from
 * {@code User.lastActiveAt} (a "recently active in app" signal), not
 * a true presence channel.
 *
 * <p><b>Backing data.</b> {@code lastActiveAt} is touched by the JWT
 * filter at most every ~5 minutes, so the "active in last 5 minutes"
 * pill is the closest thing to a real-time gauge this dataset can
 * support.
 */
const ActiveUsersPill: React.FC<ActiveUsersPillProps> = ({
  intervalMs = 30000,
  compact = false,
  windowMinutes = 5,
  popoverAlign = 'right',
}) => {
  const [data, setData] = useState<LiveNow | null>(null);
  const [users, setUsers] = useState<AdminUserDTO[]>([]);
  const [showList, setShowList] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tickRef = useRef<number | undefined>(undefined);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const sheetRadius = useSheetRadius();

  // Inner list scrolls inside the fixed sheet; avoid body overflow locks that
  // freeze nested touch-scroll on iOS.

  // Live-cohort actions (full mode only — admin pings the in-memory
  // active cohort directly without first persisting it as a segment).
  const [notifying, setNotifying] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [actionResult, setActionResult] = useState<
    | { kind: 'notify'; sent: number; missing: number; totalEligible: number; window: string }
    | { kind: 'campaign'; campaignId: number; recipients: number; window: string }
    | null
  >(null);

  // Active window — drives both the names-list query and which count
  // pill is highlighted. Initialized from the legacy {@code windowMinutes}
  // prop so existing callers ("show me names for the 5m window")
  // continue to work without a behavior change. Clicking a different
  // count pill swaps the window in place.
  const initialWindow: ActiveWindow =
    windowMinutes >= 1440 ? 'today' : windowMinutes >= 60 ? '1h' : '5m';
  const [activeWindow, setActiveWindow] = useState<ActiveWindow>(initialWindow);

  // Counts polling (always on).
  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const d = await fetchLiveNow();
        if (!mounted) return;
        setData(d);
        setError(null);
      } catch (e) {
        if (!mounted) return;
        setError(e instanceof Error ? e.message : 'live-now failed');
      }
    };

    const schedule = () => {
      const ms = document.hidden ? Math.max(intervalMs, 60000) : intervalMs;
      tickRef.current = window.setTimeout(async () => {
        await load();
        schedule();
      }, ms);
    };

    load().then(() => schedule());

    const onVis = () => {
      if (!document.hidden) load();
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      mounted = false;
      if (tickRef.current) window.clearTimeout(tickRef.current);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [intervalMs]);

  // Names polling — only while the list is open. Cheap because we cap
  // the page. Window-driven, so clicking 5m / 1h / today swaps the
  // list contents on the next tick.
  useEffect(() => {
    if (!showList) return;
    let mounted = true;
    let timer: number | undefined;

    const loadNames = async () => {
      try {
        setLoadingUsers(true);
        const minutes = windowToMinutes(activeWindow);
        const r = await fetchLiveUsers(minutes, activeWindow === 'today' ? 200 : 50);
        if (!mounted) return;
        setUsers(r?.users ?? []);
      } catch {
        /* swallow — pill still shows counts */
      } finally {
        if (mounted) setLoadingUsers(false);
      }
    };

    loadNames();
    timer = window.setInterval(loadNames, intervalMs);

    return () => {
      mounted = false;
      if (timer) window.clearInterval(timer);
    };
  }, [showList, activeWindow, intervalMs]);

  // Close compact popover on outside click.
  useEffect(() => {
    if (!compact || !showList) return;
    const onDoc = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setShowList(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [compact, showList]);

  const active5m = data?.live5m ?? 0;
  const dotClass =
    active5m > 0
      ? 'bg-emerald-500 shadow-[0_0_0_4px_rgba(16,185,129,0.18)] animate-pulse'
      : 'bg-gray-300';

  const handlePickWindow = (w: ActiveWindow) => {
    setActiveWindow(w);
    setShowList(true);
  };

  /**
   * Headline count for the *currently selected* window. Used by the
   * Notify / Email buttons so they target whatever the admin is
   * looking at (5m / 1h / today), not a fixed window.
   */
  const cohortCount: number =
    activeWindow === '5m'
      ? data?.live5m ?? 0
      : activeWindow === '1h'
        ? data?.live1h ?? 0
        : data?.today ?? 0;
  const cohortMinutes = windowToMinutes(activeWindow);
  const cohortLabel = WINDOW_LABEL[activeWindow];

  // Build a synthetic SegmentDescription so we can reuse the
  // StartCampaignModal / StartNotificationModal without forking. The
  // {@code key} matches what the backend echoes back on the response
  // ("live:Nm") so the post-send banner reads naturally for both
  // segment and live-cohort sends.
  const liveAudience: SegmentDescription = useMemo(
    () => ({
      key: `live:${cohortMinutes}m`,
      label: `Active now · ${cohortLabel}`,
      description:
        `Users with a recorded activity timestamp in the last ${cohortLabel}. ` +
        `This is an in-memory cohort — not a saved segment — so it changes every ` +
        `time the cohort window or the user activity changes.`,
      recipientCount: cohortCount,
    }),
    [cohortMinutes, cohortLabel, cohortCount],
  );

  // Pre-baked preview from the already-loaded user list, so the
  // modal can render the recipient list immediately without an
  // extra round trip to /segments/.../preview (which wouldn't
  // resolve "live:5m" anyway since it's not a persisted segment).
  const livePreview: SegmentPreview = useMemo(
    () => ({
      key: liveAudience.key,
      totalRecipients: cohortCount,
      limit: users.length,
      emails: users.map((u) => u.email).filter((e): e is string => !!e),
    }),
    [liveAudience.key, cohortCount, users],
  );

  const launchLive = async (body: CampaignLaunchBody): Promise<CampaignSummary | null> =>
    launchLiveCampaign(cohortMinutes, body);
  const notifyLive = async (message: string): Promise<SystemNotificationResult> =>
    notifyLiveUsers(cohortMinutes, message);

  const handleNotified = (r: SystemNotificationOk) => {
    setNotifying(false);
    setActionResult({
      kind: 'notify',
      sent: r.sent,
      missing: r.missing,
      totalEligible: r.totalEligible ?? r.sent + r.missing,
      window: cohortLabel,
    });
  };
  const handleLaunched = (c: CampaignSummary) => {
    setLaunching(false);
    setActionResult({
      kind: 'campaign',
      campaignId: c.id,
      recipients: c.totalRecipients ?? 0,
      window: cohortLabel,
    });
  };

  const CountPill: React.FC<{
    win: ActiveWindow;
    value: number;
    label: string;
  }> = ({ win, value, label }) => {
    const isActive = activeWindow === win && showList;
    return (
      <button
        type="button"
        onClick={() => handlePickWindow(win)}
        className={`px-1.5 py-0.5 rounded transition-colors ${
          isActive
            ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200'
            : 'hover:bg-gray-50 text-gray-600'
        }`}
        title={`Show users active ${WINDOW_LABEL[win]}`}
      >
        <span className={`font-semibold ${isActive ? 'text-emerald-900' : 'text-gray-700'}`}>
          {value}
        </span>{' '}
        <span className="text-xs">{label}</span>
      </button>
    );
  };

  const emptyMessage = `No users active ${WINDOW_LABEL[activeWindow]}.`;

  const userListItems = (
    <ul className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
      {loadingUsers && users.length === 0 && (
        <li className="py-2 text-xs text-gray-400 text-center">Loading…</li>
      )}
      {!loadingUsers && users.length === 0 && (
        <li className="py-2 text-xs text-gray-400 text-center">{emptyMessage}</li>
      )}
      {users.map((u) => {
        const signup = fmtSignupAge(u.createdAt);
        return (
          <li
            key={u.id}
            className="py-2 flex items-start gap-2 text-xs text-gray-800"
            title={`Signed up ${u.createdAt ?? '—'}\nLast active ${u.lastActiveAt ?? '—'}`}
          >
            <UserProfileAvatarLink user={u} showLiveDot={false} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <Link
                  to={getSmartProfileUrl({ id: u.id, handle: u.handle ?? undefined })}
                  className="text-[#0b3a55] hover:underline no-underline truncate max-w-[140px] font-medium"
                >
                  {u.firstname || u.handle || `#${u.id}`}
                </Link>
                {u.role && u.role !== 'USER' && (
                  <span className="text-[10px] text-gray-500">{u.role}</span>
                )}
                {signup?.isFresh && (
                  <span className="text-[10px] px-1 py-px rounded bg-emerald-100 text-emerald-800">
                    NEW
                  </span>
                )}
                <Link
                  to={`/admin/users/${u.id}`}
                  className="ml-auto shrink-0 inline-flex items-center justify-center w-5 h-5 rounded border border-gray-200 bg-gray-50 text-[10px] font-bold text-gray-500 hover:text-[#0b3a55] hover:border-gray-300"
                  title="Admin detail"
                  aria-label={`Admin detail for ${u.firstname || u.handle || u.id}`}
                >
                  A
                </Link>
              </div>
              <div className="mt-0.5 text-[10px] text-gray-500 flex items-center gap-2">
                <span>active {fmtAgo(u.lastActiveAt)}</span>
                <span className="text-gray-300">·</span>
                <span>{signup ? `signed up ${signup.text}` : 'signed up —'}</span>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );

  if (compact) {
    // Dark "ice sheet" user list — the navbar dropdown mirrors the
    // notification panel's look, so it can't reuse the light-card
    // {@code userListItems} above (that one stays for the admin page).
    const sheetUserList = (
      <div className={styles.sheetBody} onWheel={(e) => e.stopPropagation()}>
        {loadingUsers && users.length === 0 && (
          <div className={styles.empty}>Loading…</div>
        )}
        {!loadingUsers && users.length === 0 && (
          <div className={styles.empty}>{emptyMessage}</div>
        )}
        {users.map((u) => {
          const signup = fmtSignupAge(u.createdAt);
          return (
            <div
              key={u.id}
              className={styles.userRow}
              title={`Signed up ${u.createdAt ?? '—'}\nLast active ${u.lastActiveAt ?? '—'}`}
            >
              <UserProfileAvatarLink user={u} />
              <div className={styles.userMain}>
                <div className={styles.userTopLine}>
                  <Link
                    to={getSmartProfileUrl({ id: u.id, handle: u.handle ?? undefined })}
                    className={styles.userName}
                  >
                    {u.firstname || u.handle || `#${u.id}`}
                  </Link>
                  {u.role && u.role !== 'USER' && (
                    <span className={styles.userRole}>{u.role}</span>
                  )}
                  {signup?.isFresh && <span className={styles.newBadge}>NEW</span>}
                  <Link
                    to={`/admin/users/${u.id}`}
                    className={styles.adminLink}
                    title="Admin detail"
                    aria-label={`Admin detail for ${u.firstname || u.handle || u.id}`}
                  >
                    A
                  </Link>
                </div>
                <div className={styles.userMeta}>
                  <span>active {fmtAgo(u.lastActiveAt)}</span>
                  <span className={styles.userMetaSep}>·</span>
                  <span>{signup ? `signed up ${signup.text}` : 'signed up —'}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );

    return (
      <div className={styles.root} ref={popoverRef}>
        <button
          type="button"
          onClick={() => setShowList((v) => !v)}
          className={`${styles.pill}${showList ? ` ${styles.pillOpen}` : ''}`}
          aria-expanded={showList}
          title="Show who is active"
        >
          <span
            className={`${styles.dot} ${active5m > 0 ? styles.dotLive : styles.dotIdle}`}
          />
          <span className={styles.pillCount}>{active5m}</span>
          <span className={styles.pillLabel}>active</span>
        </button>
        {showList && (
          <div
            className={`${styles.sheet} ${
              popoverAlign === 'left' ? styles.sheetLeft : styles.sheetRight
            }`}
            role="dialog"
            aria-label="Active users"
          >
            <LiquidGlass
              className={styles.sheetGlass}
              shape="rounded"
              radius={sheetRadius}
              edge={0.05}
              rim={0.09}
              ripple={0.12}
              tint={0.12}
              alpha={0.82}
              fallbackVariant="modal"
            />
            <div className={styles.sheetHeader}>
              <div className={styles.sheetTitleRow}>
                <span className={styles.sheetTitle}>
                  <span
                    className={styles.sheetTitleDot}
                    style={{
                      background: active5m > 0 ? '#34d399' : 'rgba(166,206,229,0.45)',
                    }}
                  />
                  Active · {WINDOW_LABEL[activeWindow]}
                </span>
                <span className={styles.sheetAsOf}>{fmtAsOf(data?.asOf)}</span>
              </div>
              {/* Window switcher — click to swap which set of names appears below. */}
              <div className={styles.windowRow}>
                {(['5m', '1h', 'today'] as ActiveWindow[]).map((w) => {
                  const v =
                    w === '5m'
                      ? data?.live5m ?? 0
                      : w === '1h'
                      ? data?.live1h ?? 0
                      : data?.today ?? 0;
                  const isActive = activeWindow === w;
                  return (
                    <button
                      key={w}
                      type="button"
                      onClick={() => setActiveWindow(w)}
                      className={`${styles.windowChip}${
                        isActive ? ` ${styles.windowChipActive}` : ''
                      }`}
                    >
                      <span className={styles.windowChipNum}>{v}</span>
                      <span className={styles.windowChipUnit}>
                        {w === 'today' ? 'today' : w}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            {sheetUserList}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100">
      <div className="p-3 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className={`inline-block w-2.5 h-2.5 rounded-full ${dotClass}`} />
          <span className="text-xs uppercase tracking-wide text-gray-500">Active now</span>
        </div>
        {/* Primary "active in last 5 min" headline — clicking it
            opens the names list scoped to the same window. */}
        <button
          type="button"
          onClick={() => handlePickWindow('5m')}
          className={`flex items-baseline gap-2 px-1 -mx-1 rounded transition-colors ${
            activeWindow === '5m' && showList ? 'bg-emerald-50' : 'hover:bg-gray-50'
          }`}
          title="Show users active in the last 5 minutes"
        >
          <span className="text-2xl font-semibold text-gray-900">{active5m}</span>
          <span className="text-xs text-gray-500">in last 5 min</span>
        </button>
        <div className="ml-auto text-xs text-gray-500 flex items-center gap-1 flex-wrap">
          <CountPill win="1h" value={data?.live1h ?? 0} label="/1h" />
          <CountPill win="today" value={data?.today ?? 0} label="today" />
          <span className="text-gray-400 ml-1">{fmtAsOf(data?.asOf)}</span>
          <button
            type="button"
            onClick={() => setShowList((v) => !v)}
            className="ml-1 px-2 py-0.5 rounded border border-gray-200 hover:bg-gray-50 text-gray-700"
          >
            {showList ? 'Hide names' : 'Show names'}
          </button>
        </div>
        {error && <span className="text-xs text-red-500 ml-2">{error}</span>}
      </div>
      {showList && (
        <div className="px-3 pb-3 pt-1 border-t border-gray-100">
          <div className="flex items-center justify-between text-[11px] text-gray-500 mb-1 gap-2 flex-wrap">
            <span>
              Showing users active{' '}
              <span className="font-medium text-gray-700">{WINDOW_LABEL[activeWindow]}</span>
              {users.length > 0 && (
                <span className="ml-1 text-gray-400">· {users.length} shown</span>
              )}
            </span>
            <div className="inline-flex rounded border border-gray-200 overflow-hidden">
              {(['5m', '1h', 'today'] as ActiveWindow[]).map((w) => (
                <button
                  key={w}
                  type="button"
                  onClick={() => setActiveWindow(w)}
                  className={`px-2 py-0.5 text-[11px] ${
                    activeWindow === w
                      ? 'bg-emerald-50 text-emerald-800 font-medium'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {w === 'today' ? 'Today' : w}
                </button>
              ))}
            </div>
          </div>

          {/* Live-cohort actions — pings whoever is in the currently
              selected window without persisting them as a segment.
              Buttons disable when the cohort is empty so the admin
              can't fire a no-op send.
              <p>This pair lives here (rather than as a "live" preset
              segment on the Segments page) so the admin can target a
              fresh in-memory cohort. We may also expose a saved
              version on Segments later for parity with other
              segments. */}
          <div className="mb-2 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setNotifying(true)}
              disabled={cohortCount <= 0}
              className="px-2.5 py-1 text-[11px] border border-[#94c2dc] text-[#0b3a55] rounded-md hover:bg-[#eef7fb] disabled:bg-slate-100 disabled:text-slate-600 disabled:border-gray-200 disabled:cursor-not-allowed"
              title={`Send a system notification (in-app bell) to users active ${cohortLabel}`}
            >
              Notify {cohortCount.toLocaleString()}
            </button>
            <button
              type="button"
              onClick={() => setLaunching(true)}
              disabled={cohortCount <= 0}
              className="px-2.5 py-1 text-[11px] bg-[#01183a] text-white rounded-md hover:bg-[#09264f] focus-visible:ring-2 focus-visible:ring-[#94c2dc] disabled:bg-slate-200 disabled:text-slate-600 disabled:cursor-not-allowed"
              title={`Launch an email campaign to users active ${cohortLabel}`}
            >
              Email {cohortCount.toLocaleString()}
            </button>
            <span className="text-[10px] text-gray-400">
              targets the <span className="font-medium text-gray-600">{cohortLabel}</span> window ·
              not a saved segment
            </span>
          </div>

          {actionResult && (
            <div
              className={`mb-2 rounded-md p-2.5 text-[11px] flex items-start justify-between gap-2 ${
                actionResult.kind === 'notify'
                  ? 'bg-[#eef7fb] border border-[#94c2dc]/70 text-[#0b3a55]'
                  : 'bg-emerald-50 border border-emerald-200 text-emerald-900'
              }`}
            >
              <div>
                {actionResult.kind === 'notify' ? (
                  <>
                    <div className="font-semibold">
                      Notification sent to {actionResult.sent.toLocaleString()}{' '}
                      {actionResult.sent === 1 ? 'user' : 'users'} active {actionResult.window}
                    </div>
                    <div className="opacity-80">
                      {actionResult.missing > 0 && (
                        <>{actionResult.missing.toLocaleString()} missing · </>
                      )}
                      {actionResult.totalEligible.toLocaleString()} resolved before filtering · in-app
                      bell only
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-semibold">
                      Campaign #{actionResult.campaignId} queued to{' '}
                      {actionResult.recipients.toLocaleString()} recipients active{' '}
                      {actionResult.window}
                    </div>
                    <div className="opacity-80">
                      Daily-throttled · suppression-aware · same path as /contact-email.
                    </div>
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={() => setActionResult(null)}
                className="text-current opacity-60 hover:opacity-100 leading-none text-base"
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
          )}

          {userListItems}
        </div>
      )}

      {notifying && (
        <StartNotificationModal
          segment={liveAudience}
          initialPreview={livePreview}
          skipDefaultPreview
          notify={notifyLive}
          onClose={() => setNotifying(false)}
          onSent={handleNotified}
        />
      )}
      {launching && (
        <StartCampaignModal
          segment={liveAudience}
          initialPreview={livePreview}
          skipDefaultPreview
          launch={launchLive}
          onClose={() => setLaunching(false)}
          onLaunched={handleLaunched}
        />
      )}
    </div>
  );
};

export default ActiveUsersPill;
